<script lang="ts">
	import { type EmailAddress, EmailType, type MailProtocolEntryDto } from './MailProtocol.model'
	import { unixToLocalDate, unixToLocalTime } from '$utils/formatters'
	import { modals } from 'svelte-modals'
	import { t } from '$i18n/i18n'
	import { closeModalOnOutsideClick } from '../stores'
	import MailConfigModal from './MailConfigModal.svelte'
	import MailStatusBadge from './MailStatusBadge.svelte'
	import { formatJsonToPrettyHtml } from '$utils/utils.svelte'

	export let protocolData: MailProtocolEntryDto
	const { subject, sender, recipients, sendUnixTimestamp, status, statusMessage, mailHtml } =
		protocolData

	function formatRecipientsList() {
		const to = recipients.filter((r) => r.type === EmailType.TO)
		const cc = recipients.filter((r) => r.type === EmailType.CC)
		const bcc = recipients.filter((r) => r.type === EmailType.BCC)
		let result = ''

		function formatEmailAddress(r: EmailAddress) {
			if (r?.name) {
				return `${r.name} (${r.mail})`
			}
			return r.mail
		}

		if (to.length > 0) {
			result += '<b>Versendet an:</b><br>' + to.map(formatEmailAddress).join('<br>')
		}
		if (cc.length > 0) {
			if (result.length > 0) {
				result += '<br>'
			}
			result += '<b>CC:</b><br>' + cc.map(formatEmailAddress).join('<br>')
		}
		if (bcc.length > 0) {
			if (result.length > 0) {
				result += '<br>'
			}
			result += '<b>BCC:</b><br>' + bcc.map(formatEmailAddress).join('<br>')
		}
		return result
	}

	function handleOpenMail() {
		$closeModalOnOutsideClick = true
		modals.open(MailConfigModal, {
			title: subject || '-',
			content: formatJsonToPrettyHtml(mailHtml),
			ttContent: formatRecipientsList()
		})
	}
</script>

<div class="mail-protocol-entry-container">
	<div class="mail-protocol-entry__status">
		<MailStatusBadge {status} {statusMessage} />
		<h6>{unixToLocalDate(sendUnixTimestamp)}</h6>
		<h6>{unixToLocalTime(sendUnixTimestamp)}</h6>
	</div>
	<div class="mail-protocol-entry__subject">
		<h4>{subject || '-'}</h4>
	</div>
	<div class="mail-protocol-entry__sender">
		{#if sender?.name}
			<h4>{sender.name}</h4>
		{/if}
		{#if sender?.mail}
			<h4>{sender.mail}</h4>
		{/if}
	</div>
	<div class="mail-protocol-entry__recipient">
		{#if recipients[0]?.name}
			<h4>{recipients[0].name}</h4>
		{/if}
		<h4>
			{recipients[0].mail}
			{#if recipients.length > 1}
				<a on:click={handleOpenMail}>+{recipients.length}&nbsp;weitere</a>
			{/if}
		</h4>
	</div>
	<div class="mail-protocol-entry-mail-html">
		<h6 on:click={handleOpenMail}>{$t('UI.mail.protocol.showMail')}</h6>
	</div>
</div>

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';

	.mail-protocol-entry-container {
		display: grid;
		grid-template-columns: subgrid;
		grid-column: 1/-1;
		border-bottom: 1px solid global.$grey-700;
		min-height: 4rem;
		padding: 0.75rem 0;

		& div {
			place-self: start start;
		}

		& h4 {
			margin: 0;

			// word-break: break-word;
		}

		&:last-child {
			border-bottom: none;
		}

		background-color: white;
	}

	.mail-protocol-entry__status {
		grid-column: 1 / 2;

		@include global.flex-col(0.125rem, $alignment: flex-start);

		& > h6 {
			color: #999;
			margin: 0;
		}
	}

	.mail-protocol-entry__subject {
		grid-column: 2 / 3;
	}

	.mail-protocol-entry__sender {
		grid-column: 3 / 4;
	}

	.mail-protocol-entry__recipient {
		grid-column: 4 / 5;

		& a {
			@include global.roboto-font(normal, 400, 0.875rem, global.$primaryGreen);

			text-decoration: none;
			margin: 0;
			cursor: pointer;
		}
	}

	.mail-protocol-entry-mail-html {
		grid-column: 5 / -1;

		& h6 {
			margin: 0;
			color: global.$primaryGreen;
			cursor: pointer;
		}
	}

	@media screen and (max-width: breakpoints.$xsmall) {
		h6 {
			font-size: 0.625rem;
		}

		h4 {
			font-size: 0.75rem;
		}

		.mail-protocol-entry__recipient a {
			font-size: 0.75rem;
		}
	}
</style>
